'use client';

import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { Button, Form } from 'reactstrap';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';

import { SendMessageButton } from 'src/assets/images/job-details-slide-out/job-details-slide-out';
import CustomInputField from 'src/components/shared/form-fields/custom-input-field';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { sendMessage, uploadCustomerChatAttachment } from 'src/redux/jobs/api';
import { updateReduxMessageState } from 'src/redux/jobs/slice';
import { createAbortSignal, generateRandomString } from 'src/helpers/utils';
import { Tooltip } from 'react-tooltip';
import ChatFileUploader from 'src/components/shared/form-fields/chat-file-uploader';
import ImagePreviewListing from 'src/components/shared/image-preview-listing';

const MAX_FILE = 8;

interface ChatAttachment {
  media_id: string;
  isImageUploading: boolean;
  image: File;
  mime_type?: string;
  attachment_id?: number;
  [key: string]: unknown;
}

interface MessageFormValues {
  inputMessage: string;
  attachments: ChatAttachment[];
  newAttachments: File[];
  deleted_attachments: number[];
}

const focusChatInputField = () => {
  const element = document?.getElementsByName('inputMessage')?.[0] as HTMLElement | undefined;
  if (element) element.focus();
};

const MessageFooter: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeThreadId = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.activeThreadId,
  ) as number | undefined;
  const meta = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.meta,
  ) as Record<string, unknown> | undefined;

  const serviceRequestId = useAppSelector(
    (state) => state?.jobDetails?.data
      ?.service_request_id,
  );
  const isFetchingMessages = useAppSelector((state) => state?.jobDetails?.isFetchingMessages);
  const isFetchingMessageThreads = useAppSelector(
    (state) => state?.jobDetails?.isFetchingMessageThreads,
  );

  const methods = useForm<MessageFormValues>({
    mode: 'all',
    defaultValues: {
      inputMessage: '',
      attachments: [],
      newAttachments: [],
      deleted_attachments: [],
    },
  });
  const isChatDisabled = useAppSelector(
    (state) => {
      const msgs = state?.jobDetails?.messages as Record<string, unknown>;
      const disabled = msgs?.isChatDisabled as Record<string, unknown>;
      return disabled?.isChatDisabled;
    },
  ) as boolean | undefined;

  const chatDisabledReason = useAppSelector(
    (state) => {
      const msgs = state?.jobDetails?.messages as Record<string, unknown>;
      const disabled = msgs?.isChatDisabled as Record<string, unknown>;
      return disabled?.chatDisabledReason;
    },
  ) as string | undefined;
  const {
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = methods;
  const [messageValue, attachments] = watch(['inputMessage', 'attachments']);
  const sendEnabled = !!messageValue?.trim().length || !!attachments.length;
  const isImageUploading = attachments?.some((attachment) => (attachment.isImageUploading));
  const controllerRef = useRef<Record<string, AbortController>>({});

  useEffect(() => {
    if (!isEmpty(errors?.newAttachments?.types)) {
      if (Object.hasOwn(
        errors?.newAttachments?.types || {},
        'file-invalid-type',
      )) {
        const msg = 'This file format is not supported  only jpg, jpeg, and png formats are accepted.';
        toast.error(msg);
      }
    }
  }, [errors?.newAttachments?.types]);

  const updateAttachments = useCallback(({ media, res = {}, currentAttachments }: {
    media: ChatAttachment;
    res?: Record<string, unknown>;
    currentAttachments?: ChatAttachment[];
  }) => {
    const updatedAttachments = (
      currentAttachments || getValues('attachments')
    )?.map((updatedMedia) => {
      if (media?.media_id === updatedMedia?.media_id) {
        return {
          ...updatedMedia,
          ...res,
          mime_type: (res?.mime_type as string) || '',
          isImageUploading: false,
        };
      }
      return updatedMedia;
    });
    setValue('attachments', updatedAttachments as ChatAttachment[]);
  }, [getValues, setValue]);

  const handleImagePicker = useCallback(() => {
    const selectedImages = getValues('newAttachments');
    const previousAttachment = getValues('attachments');
    if (MAX_FILE + 1 <= (selectedImages.length + previousAttachment.length)) {
      toast.error(`You can only attach ${MAX_FILE} Files!`, { theme: 'colored' });
      return;
    }
    const updatedSelectedMedias = selectedImages?.map((image) => {
      const mediaId = generateRandomString();
      return {
        image,
        media_id: mediaId,
        isImageUploading: true,
      };
    });
    setValue('attachments', [...previousAttachment, ...updatedSelectedMedias] as ChatAttachment[]);

    updatedSelectedMedias?.forEach((media) => {
      const attachmentPayload = new FormData();
      attachmentPayload.append('file', media?.image);
      const controller = createAbortSignal();
      controllerRef.current = { ...controllerRef.current, [media?.media_id]: controller };
      dispatch(uploadCustomerChatAttachment(
        serviceRequestId,
        activeThreadId != null ? String(activeThreadId) : undefined,
        controller?.signal,
      )(attachmentPayload)).unwrap()
        .then((rawRes: unknown) => {
          const res = rawRes as Record<string, unknown>;
          updateAttachments({ media: media as ChatAttachment, res });
        })
        .catch((error: Record<string, unknown>) => {
          if (error?.actionType !== 'canceled') {
            const attachmentsAfterDeletion = attachments?.filter(
              (attachment) => attachment?.media_id !== media?.media_id,
            );
            updateAttachments({
              media: media as ChatAttachment,
              currentAttachments: attachmentsAfterDeletion,
            });
          }
        });
    });
  }, [activeThreadId,
    attachments,
    dispatch,
    getValues,
    serviceRequestId,
    setValue,
    updateAttachments]);

  const onMessageSend = useCallback((data: MessageFormValues) => {
    const attachmentsToSend = data.attachments || [];
    if (sendEnabled) {
      reset();
      const messageId = generateRandomString();
      const attachmentIds = data?.attachments?.map((attachment) => (attachment?.attachment_id));
      const attachmentsReduxPayload = attachmentsToSend.map((attachmentToReduxPayload) => ({
        attachment_local_url: URL.createObjectURL(attachmentToReduxPayload.image),
        media_id: attachmentToReduxPayload.media_id,
        mime_type: attachmentToReduxPayload.image?.type?.replace('image/', ''),
        file_name: attachmentToReduxPayload.image?.name,
        size: attachmentToReduxPayload.image?.size,
        file: attachmentToReduxPayload.image,
        attachment_id: attachmentToReduxPayload?.attachment_id,
      }));
      const reduxDataPayload = {
        message: data?.inputMessage?.trim(),
        message_id: messageId,
        isSending: true,
        direction: 'outbound',
        attachments: attachmentsReduxPayload,
      };
      dispatch(updateReduxMessageState({
        data: reduxDataPayload,
        date: 'Today',
        actionType: 'add',
      }));
      const threadId = activeThreadId != null
        ? String(activeThreadId) : undefined;
      dispatch(sendMessage(serviceRequestId, threadId)({
        message: data?.inputMessage?.trim(),
        attachments: attachmentIds,
        ...(data?.deleted_attachments?.length && {
          deleted_attachments: data?.deleted_attachments,
        }),
        activeThreadId,
      })).unwrap().then((rawResponse: unknown) => {
        const response = rawResponse as Record<string, unknown>;
        const hasAttachments = Array.isArray(response.attachments)
        && !isEmpty(response.attachments);
        const updatedMessage = {
          ...(response || {}),
          isSending: false,
          ...(hasAttachments && {
            attachments: reduxDataPayload?.attachments,
          }),
        };
        dispatch(updateReduxMessageState({
          data: updatedMessage,
          date: 'Today',
          actionType: 'update',
          tempId: messageId,
        }));
      }).catch((error: Record<string, unknown>) => {
        dispatch(updateReduxMessageState({
          data: {
            ...(reduxDataPayload || {}),
            isSending: false,
            hasFailed: true,
            attachments: reduxDataPayload?.attachments,
          },
          date: 'Today',
          actionType: 'update',
          tempId: messageId,
        }));
        toast(
          (error?.error as string)
          || (error?.errors as Record<string, string[]>)
            ?.general?.[0]
          || 'Something went wrong',
          { type: 'error' },
        );
      });
    }
  }, [
    sendEnabled,
    reset,
    dispatch,
    serviceRequestId,
    activeThreadId,
  ]);

  useEffect(() => {
    focusChatInputField();
  }, []);

  useEffect(() => {
    const element = document.getElementById('message-field');
    if (element) {
      const computedStyle = window.getComputedStyle(element);
      const slideEl = document
        ?.getElementsByClassName(
          'job-details-slide',
        )[0] as HTMLElement;
      slideEl?.style.setProperty(
        '--slide-out-footer-height',
        `${computedStyle.height}`,
      );
    }
  }, []);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onMessageSend)}>
        <div className="message-field" id="message-field">
          <ImagePreviewListing onDeleteImage={(mediaId: string) => {
            if (controllerRef.current?.[mediaId]) {
              controllerRef.current?.[mediaId]?.abort();
            }
          }}
          />
          <div className="message-field-wrapper">
            <CustomInputField
              className="form-control"
              name="inputMessage"
              placeholder="Write your message here"
              type="textarea"
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === 'Enter' && !e.shiftKey && messageValue?.trim().length
                && !isChatDisabled && !isFetchingMessages && !isFetchingMessageThreads
                && !isEmpty(meta)
                ) {
                  e.preventDefault();
                  methods.handleSubmit(onMessageSend)();
                }
              }}
            />
            <div className="action-wrapper">
              <ChatFileUploader
                handleImagePicker={handleImagePicker}
                disable={isChatDisabled
                || (isFetchingMessages && isEmpty(meta))
                || isFetchingMessageThreads}
              />
              <Button
                className="send-btn"
                color="deco"
                type="submit"
                disabled={
                (!sendEnabled
                || isChatDisabled
                || (isFetchingMessages && isEmpty(meta))
                || isFetchingMessageThreads || isImageUploading) as boolean
              }
                {...(isChatDisabled ? {
                  'data-tooltip-id': 'send-button',
                  'data-tooltip-content': '',
                } : {})}
              >
                <SendMessageButton />
                Send
              </Button>
              {isChatDisabled && !isEmpty(chatDisabledReason)
                ? (
                  <Tooltip
                    id="send-button"
                    className="chat-send-disabled-tooltip"
                    positionStrategy="fixed"
                    opacity={1}
                    render={() => (
                      <p>{chatDisabledReason}</p>
                    )}
                  />
                ) : ''}
            </div>
          </div>
        </div>
      </Form>
    </FormProvider>
  );
};

export default MessageFooter;
